/**
 * @file sheets/shared/utils.ts
 *
 * Shared utility helpers for Bottom Sheet and Side Sheet components.
 */

import { DEFAULT_SPATIAL_SPRING } from "../../shared/motion-tokens";
import type { SheetEdge } from "./types";

// ─── Divider resolution ───────────────────────────────────────────────────────

type DividerProp = boolean | { header?: boolean; footer?: boolean } | undefined;

/**
 * Normalizes the `divider` prop into an explicit `{ header, footer }` object.
 */
export function resolveDividers(divider: DividerProp): {
	header: boolean;
	footer: boolean;
} {
	if (divider === false) return { header: false, footer: false };
	if (divider === undefined || divider === true) {
		return { header: true, footer: true };
	}
	return { header: divider.header ?? true, footer: divider.footer ?? true };
}

// ─── Sheet animation ──────────────────────────────────────────────────────────

/**
 * Builds slide-in/out animation config for a Side Sheet based on its edge.
 */
export function buildSideSheetAnim(edge: SheetEdge) {
	const exitX = edge === "end" ? "100%" : "-100%";
	return {
		initial: { x: exitX, opacity: 0.6 },
		animate: { x: 0, opacity: 1, transition: DEFAULT_SPATIAL_SPRING },
		exit: {
			x: exitX,
			opacity: 0,
			transition: { duration: 0.22, ease: "easeIn" as const },
		},
	} as const;
}

// ─── Default Sheet Styles ──────────────────────────────────────────────────────

/**
 * Default background color class name for Bottom and Side Sheets.
 * Follows MD3 surfaceContainerLow token.
 */
export const DEFAULT_SHEET_BG = "bg-m3-surface-container-low";
